import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import {
  Button,
  Callout,
  Container,
  AppError,
  FieldError,
  FlexRow,
  H1,
  H2,
  Label,
  Select,
  Text,
  AllCaps,
  PageHeader,
  Box,
  Stack,
} from "atoms";
import { constants } from "config";
import { useAuth } from "features/auth";
import { ApiError } from "utils/api";
import { fieldOptions } from "utils/form";
import { useToggle } from "utils/hooks";
import {
  useModeratorFeedbackForm,
  useUpdateReviewVote,
} from "../api/useReviewVotes";
import type { ProjectReview, ArtistReview } from "../types";
import { Review } from "./Review";

const downvoteReasons = ["Inappropriate or offensive", "Looks like spam"];

const countdownDuration = 6;
const title = "Moderate Reviews";
const introText = (
  <Text>
    Your opinion matters! <AllCaps>Hug</AllCaps> is a community-curated
    platform. That means every artist and artist review on{" "}
    <AllCaps>Hug</AllCaps> has been signed off by artists and art lovers like
    you.
  </Text>
);

const moderateText = (
  <>
    <Text size="xs">
      To nurture a supportive environment for artists and art lovers, reviews
      ought to be descriptive, respectful, and true to the reviewer&apos;s
      personal experience.
    </Text>
    <Text size="xs">
      If a review adheres to these guidelines, answer “Yes” to get it published.
    </Text>
    <Text size="xs">
      If it does not, or something about the review doesn&apos;t seem right,
      answer “No” to flag the review and let us know why.
    </Text>
    <Text size="xs">
      Each review you moderate earns you {constants.pointsEarnedModeratorVote}{" "}
      <AllCaps>Hug</AllCaps> Points. Have fun, and thank you for supporting
      artists here on <AllCaps>Hug</AllCaps>!
    </Text>
  </>
);

interface ModerateIntroProps {
  userHasAgreed: boolean;
  onSubmit: VoidFunction;
}

function ModerateIntro({ userHasAgreed, onSubmit }: ModerateIntroProps) {
  const [showHowTo, toggleHowTo] = useToggle(false);

  if (!userHasAgreed) {
    return (
      <>
        <PageHeader overlap>
          <Container width="sm">
            <H1 size="h2">{title}</H1>
          </Container>
        </PageHeader>
        <Container width="sm" style={{ paddingBottom: "3rem" }}>
          <Box fullWidth>
            <Text bold textAlign="center">
              {introText}
            </Text>

            <Text as="div" textAlign="center">
              {moderateText}
            </Text>

            <Text textAlign="center">
              <Button
                onClick={onSubmit}
                type="button"
                variant="secondary"
                size="md"
              >
                I&apos;m Ready to Moderate
              </Button>
            </Text>
          </Box>
        </Container>
      </>
    );
  }
  return (
    <Container width="md">
      <Title>
        <H1 size="h2">{title}</H1>

        {!showHowTo && (
          <Button size="xs" variant="secondary" onClick={toggleHowTo}>
            How to Moderate Reviews
          </Button>
        )}
      </Title>

      {showHowTo && (
        <Intro justifyContent="space-between" gap="0 1rem">
          <HowToPopup>
            <H2 size="h3">How to Moderate Reviews</H2>

            <div>
              <Button size="xs" variant="secondary" onClick={toggleHowTo}>
                Close
              </Button>

              <Text size="xs">{introText}</Text>

              {moderateText}
            </div>
          </HowToPopup>
        </Intro>
      )}
    </Container>
  );
}

interface ModerateMainProps {
  review?: ArtistReview | ProjectReview;
  reviewsAvailable: number;
  isDownvoteFormShowing: boolean;
  toggleDownvoteForm: VoidFunction;
}

function ModerateMain({
  review,
  reviewsAvailable,
  isDownvoteFormShowing,
  toggleDownvoteForm,
}: ModerateMainProps) {
  return (
    <Container width="md">
      <VotingMain>
        {!review ? (
          <Callout>
            <CaughtUpFlexRow>
              <div>
                <H2>We&apos;re all caught up!</H2>

                <Text size="xs">
                  All pending reviews have been&nbsp;moderated!
                </Text>
              </div>
            </CaughtUpFlexRow>
          </Callout>
        ) : (
          <>
            <ArtistCount size="xs" bold>
              There are <strong>{reviewsAvailable}</strong> reviews waiting
              for&nbsp;moderation.
            </ArtistCount>

            <ReviewWrapper>
              <Review
                review={review}
                aria-live="polite"
                showVotes={false}
                showParent
              />
            </ReviewWrapper>

            {isDownvoteFormShowing && review && (
              <ModerateFeedbackForm
                review={review}
                toggleDownvoteForm={toggleDownvoteForm}
              />
            )}
          </>
        )}
      </VotingMain>
    </Container>
  );
}

type ModerateFeedbackFormProps = {
  review: ArtistReview | ProjectReview;
  toggleDownvoteForm: VoidFunction;
};

function ModerateFeedbackForm({
  review,
  toggleDownvoteForm,
}: ModerateFeedbackFormProps) {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useModeratorFeedbackForm();

  const { updateReviewVote } = useUpdateReviewVote({
    reviewId: review.reviewId,
    reviewableType: review.reviewableType,
    reviewableId: review.reviewableId,
  });

  const handleModeratorFeedback = handleSubmit((formData) => {
    updateReviewVote(
      { moderatorVote: true, ...formData },
      {
        onSuccess: () => {
          toggleDownvoteForm();
        },
      }
    );
  });

  return (
    <Box>
      <DownvoteForm onSubmit={handleModeratorFeedback}>
        <Label>
          <Stack>
            <Text size="md" bold>
              Why are you flagging this review?
            </Text>

            <Select
              {...register("moderatorFeedback", {
                ...fieldOptions.required,
              })}
            >
              <option value="" disabled>
                -- Select a reason --
              </option>
              {downvoteReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </Select>

            {errors.moderatorFeedback && (
              <FieldError>{errors.moderatorFeedback.message}</FieldError>
            )}
          </Stack>
        </Label>
        <FlexRow justifyContent="center">
          <Button onClick={toggleDownvoteForm} variant="secondary" size="sm">
            Cancel
          </Button>

          <Button type="submit" variant="primary" size="sm" arrow="r">
            Submit
          </Button>
        </FlexRow>
      </DownvoteForm>
    </Box>
  );
}

interface ModerateFooterProps {
  review: ArtistReview | ProjectReview;
  toggleDownvoteForm: VoidFunction;
}

function ModerateFooter({ review, toggleDownvoteForm }: ModerateFooterProps) {
  const {
    user,
    roles: { isHugger },
  } = useAuth();

  const { updateReviewVote, isLoading, isSuccess, isError, error } =
    useUpdateReviewVote({
      reviewId: review.reviewId,
      reviewableType: review.reviewableType,
      reviewableId: review.reviewableId,
    });

  const isUsersReview = user?.id === review?.createdByUserId;

  // A user can't vote on their own review or if they're not a HUG Pass holder
  const isNotVoter = isUsersReview || !isHugger;

  const handleUpvote = () => {
    updateReviewVote({ vote: "upvote", moderatorVote: true });
  };

  return (
    <>
      {isSuccess && (
        <SuccessMessage aria-live="polite">
          <Text size="xs" nowrap>
            Thanks for your input!
          </Text>

          <Text size="xs" nowrap>
            You get {constants.pointsEarnedModeratorVote} <AllCaps>Hug</AllCaps>{" "}
            Points.
          </Text>
        </SuccessMessage>
      )}

      <FooterFlexRow>
        <FlexRow>
          <Text bold>Should this review get published?</Text>
          <Button
            type="button"
            onClick={handleUpvote}
            disabled={isNotVoter || isLoading}
            variant="primary"
            size="sm"
          >
            Yes
          </Button>

          <Button
            type="button"
            onClick={toggleDownvoteForm}
            disabled={isNotVoter || isLoading}
            variant="primary"
            size="sm"
          >
            No
          </Button>

          {isError && (
            <AppError>
              {error instanceof ApiError
                ? error.message
                : "Something went wrong"}
            </AppError>
          )}
        </FlexRow>
      </FooterFlexRow>
    </>
  );
}

const Title = styled.div(({ theme }) => ({
  paddingTop: "2rem",
  [theme.breakpoints.desktop]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    h1: {
      marginBottom: 0,
    },
  },
}));

const Intro = styled(FlexRow)(({ theme }) => ({
  position: "relative",
  alignItems: "flex-start",
  marginTop: "1rem",
  [theme.breakpoints.tablet]: {
    "&>p": {
      flex: 1,
    },
  },
}));

const HowToPopup = styled.div(({ theme }) => ({
  background: theme.colors.fg05,
  padding: "3.5rem 1rem 1.5rem",
  borderRadius: theme.borderRadius.sm,
  position: "relative",
  [theme.breakpoints.desktop]: {
    padding: "1.5rem",
    paddingRight: "7rem",
  },
  img: {
    width: "60%",
    maxWidth: 220,
    marginRight: "auto",
    [theme.breakpoints.desktop]: {
      margin: "0 auto",
    },
  },
  button: {
    position: "absolute",
    top: "1rem",
    right: "1rem",
  },
}));

const VotingMain = styled.div({
  margin: "2rem 0",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const ReviewWrapper = styled.div(({ theme }) => ({
  borderRadius: theme.borderRadius.md,
  border: `1px solid ${theme.colors.fg20}`,
  padding: "1rem",
}));

const ArtistCount = styled(Text)(({ theme }) => ({
  color: theme.colors.fg,
  margin: 0,
  strong: {
    ...theme.fonts.display,
    borderRadius: theme.borderRadius.round,
    background: theme.colors.accent3L,
    padding: "2px 4px",
    lineHeight: 1,
  },
}));

const CaughtUpFlexRow = styled(FlexRow)(({ theme }) => ({
  alignItems: "center",
  textAlign: "center",
  justifyContent: "center",
  gap: "1rem 3rem",
  [theme.breakpoints.desktop]: {
    textAlign: "left",
  },
  img: {
    maxWidth: 150,
    margin: "0 auto",
    [theme.breakpoints.desktop]: {
      margin: 0,
      maxWidth: 190,
    },
  },
  div: {
    width: 450,
    maxWidth: "100%",
    margin: "0 auto",
    [theme.breakpoints.desktop]: {
      flex: 1,
      margin: 0,
    },
  },
}));

const FooterFlexRow = styled(FlexRow)(({ theme }) => ({
  color: theme.colors.fg,
  gap: "1.25rem 3rem",
  justifyContent: "center",
  textAlign: "center",
  p: {
    margin: 0,
  },
}));

const messageInOut = keyframes({
  "0%, 100%": {
    opacity: 0,
    transform: "translate(-50%, 100%)",
  },
  "5%, 95%": {
    opacity: 1,
    transform: "translate(-50%, 0)",
  },
});

const SuccessMessage = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: 5,
  backgroundColor: theme.colors.green,
  borderRadius: theme.borderRadius.round,
  border: `2px solid ${theme.colors.greenDark}`,
  boxShadow: theme.boxShadow.dark,
  color: theme.colors.fg,
  fontSize: theme.fontSizes.xxs,
  lineHeight: 1.3,
  marginBottom: 0,
  padding: ".4em 1em .5em",
  position: "absolute",
  left: "50%",
  bottom: "calc(100% + .5rem)",
  transform: "translate(-50%, 0)",
  minWidth: 250,
  maxWidth: "calc(100vw - 2rem)",
  pointerEvents: "none",
  zIndex: 3,
  animation: `${countdownDuration}s ease 0s forwards ${messageInOut}`,
  svg: {
    width: 13,
  },
  p: {
    margin: 0,
  },
}));

const DownvoteForm = styled.form({
  display: "grid",
  alignItems: "center",
  justifyContent: "center",
  width: "max-content",
  margin: "0 auto",
  label: {
    marginBottom: "1rem",
    textAlign: "center",
    display: "grid",
    alignItems: "center",
    justifyContent: "center",
    "& *": {
      width: "100%",
    },
  },
});

export { ModerateIntro, ModerateMain, ModerateFooter };
